import React, { useState, useEffect } from 'react';
import { FiZap, FiAlertCircle, FiCheckCircle, FiCpu } from 'react-icons/fi';
import ImageUpload from './components/ImageUpload';
import ImageCrop from './components/ImageCrop';
import BackgroundOptions from './components/BackgroundOptions';
import Preview from './components/Preview';
import Result from './components/Result';
import { processImage, autoProcessImage, getImageUrl, getPassportCountries, getFilenameFromPath } from './services/api';
import './SelfieEditor.css';

/**
 * SelfieEditor Component
 * Manages state and coordinates all components
 */
function SelfieEditor() {
  // State management
  const [selectedImage, setSelectedImage] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const [backgroundType, setBackgroundType] = useState('transparent');
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [passportSize, setPassportSize] = useState(false);
  const [customWidth, setCustomWidth] = useState('');
  const [customHeight, setCustomHeight] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [countriesList, setCountriesList] = useState([]);
  const [countrySizes, setCountrySizes] = useState({});
  const [loading, setLoading] = useState(false);
  const [resultImageUrl, setResultImageUrl] = useState(null);
  const [outputFilename, setOutputFilename] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [processingMode, setProcessingMode] = useState('manual'); // 'manual' or 'auto'
  const [autoCrop, setAutoCrop] = useState(true);

  /**
   * Handle image selection
   */
  const handleImageSelect = (file) => {
    setSelectedImage(file);
    setResultImageUrl(null);
    setOutputFilename(null);
    setError(null);
    setSuccess(false);
  };

  /**
   * Handle background type change
   */
  const handleBackgroundTypeChange = (type) => {
    setBackgroundType(type);
    setResultImageUrl(null);
    setError(null);
  };

  /**
   * Handle background color change
   */
  const handleBackgroundColorChange = (color) => {
    setBackgroundColor(color);
  };

  // Fetch passport countries list on component mount
  useEffect(() => {
    let isMounted = true;
    
    const fetchCountries = async () => {
      try {
        const response = await getPassportCountries();
        if (isMounted && response?.countries) {
          const countryNames = Object.keys(response.countries).sort();
          setCountriesList(countryNames);
          setCountrySizes(response.countries);
        }
      } catch (error) {
        // Only log if component is still mounted and it's not a cancellation
        if (isMounted) {
          console.error('Error loading passport countries:', error);
          // Fallback - use empty list, don't show error to user
        }
      }
    };
    
    fetchCountries();
    
    // Cleanup
    return () => {
      isMounted = false;
    };
  }, []);

  /**
   * Handle crop button click
   */
  const handleCropClick = () => {
    if (selectedImage) {
      setShowCropModal(true);
    }
  };

  /**
   * Handle crop complete
   */
  const handleCropComplete = (croppedImageFile) => {
    setSelectedImage(croppedImageFile);
    setShowCropModal(false);
    setResultImageUrl(null);
    setOutputFilename(null);
    setError(null);
    setSuccess(false);
  };

  /**
   * Handle crop cancel
   */
  const handleCropCancel = () => {
    setShowCropModal(false);
  };

  /**
   * Validate form before submission
   */
  const validateForm = () => {
    if (!selectedImage) {
      setError('Please select an image to process');
      return false;
    }

    if (backgroundType === 'color' && !backgroundColor) {
      setError('Please select a background color');
      return false;
    }

    return true;
  };

  /**
   * Process image via API (Manual Mode)
   */
  const handleManualProcess = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError(null);
    setSuccess(false);
    setResultImageUrl(null);
    setOutputFilename(null);

    try {
      const formData = new FormData();
      formData.append('image', selectedImage);
      formData.append('remove_background', 'true');
      formData.append('background_type', backgroundType);
      formData.append('passport_size', passportSize.toString());

      if (backgroundType === 'color' && backgroundColor) {
        formData.append('background_value', backgroundColor);
      }

      if (selectedCountry) {
        formData.append('country', selectedCountry);
      } else if (customWidth && customHeight) {
        formData.append('custom_width', customWidth);
        formData.append('custom_height', customHeight);
      }

      const response = await processImage(formData);

      if (response.status === 'success') {
        const imageUrl = getImageUrl(response.output_image);
        setResultImageUrl(imageUrl);
        setOutputFilename(getFilenameFromPath(response.output_image));
        setSuccess(true);
      } else {
        setError(response.message || 'Failed to process image');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while processing the image');
      console.error('Error processing image:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Auto process image via API (Auto Mode)
   */
  const handleAutoProcess = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError(null);
    setSuccess(false);
    setResultImageUrl(null);
    setOutputFilename(null);

    try {
      const formData = new FormData();
      formData.append('image', selectedImage);
      formData.append('background_type', backgroundType);
      formData.append('auto_crop', autoCrop.toString());
      formData.append('auto_resize', 'true');

      if (backgroundType === 'color' && backgroundColor) {
        formData.append('background_value', backgroundColor);
      }

      // Determine target size
      if (selectedCountry) {
        formData.append('target_size', 'country');
        formData.append('country', selectedCountry);
      } else if (customWidth && customHeight) {
        formData.append('target_size', 'custom');
        formData.append('custom_width', customWidth);
        formData.append('custom_height', customHeight);
      } else if (passportSize) {
        formData.append('target_size', 'passport');
      } else {
        formData.append('target_size', 'passport'); // Default
      }

      const response = await autoProcessImage(formData);

      if (response.status === 'success') {
        const imageUrl = getImageUrl(response.output_image);
        setResultImageUrl(imageUrl);
        setOutputFilename(getFilenameFromPath(response.output_image));
        setSuccess(true);
      } else {
        setError(response.message || 'Failed to auto-process image');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while auto-processing the image');
      console.error('Error auto-processing image:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle process button click based on mode
   */
  const handleProcessImage = () => {
    if (processingMode === 'auto') {
      handleAutoProcess();
    } else {
      handleManualProcess();
    }
  };

  /**
   * Reset form to process another image
   */
  const handleReset = () => {
    setSelectedImage(null);
    setBackgroundType('transparent');
    setBackgroundColor('#ffffff');
    setPassportSize(false);
    setCustomWidth('');
    setCustomHeight('');
    setSelectedCountry('');
    setResultImageUrl(null);
    setOutputFilename(null);
    setError(null);
    setSuccess(false);
  };

  // Get selected country size info
  const selectedCountrySize = selectedCountry && countrySizes[selectedCountry];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      {/* Header */}
      <div className="mb-10 text-center md:text-left">
        <h1 className="text-4xl md:text-5xl font-bold mb-3 text-gray-900 dark:text-white">
          Passport Photo Editor
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-400">
          Remove or replace backgrounds from your photos with AI - Perfect for passport & ID photos
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Left Column - Tools & Image Upload */}
        <div className="lg:col-span-5 space-y-6">
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
            {/* Processing Mode Toggle */}
            <div className="p-4 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20">
              <div className="flex items-center gap-2 mb-3">
                <FiCpu className="text-blue-600" />
                <span className="text-sm font-bold text-gray-700 dark:text-gray-200">Processing Mode</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setProcessingMode('manual')}
                  className={`py-2.5 px-4 rounded-xl text-sm font-bold transition-all ${
                    processingMode === 'manual'
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'bg-white dark:bg-slate-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-600'
                  }`}
                >
                  Manual
                </button>
                <button
                  onClick={() => setProcessingMode('auto')}
                  className={`py-2.5 px-4 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-1 ${
                    processingMode === 'auto'
                      ? 'bg-purple-600 text-white shadow-lg'
                      : 'bg-white dark:bg-slate-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-600'
                  }`}
                >
                  <FiZap size={14} /> Auto AI
                </button>
              </div>
              {processingMode === 'auto' && (
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Auto-crop, resize, and background removal in one click
                </p>
              )}
            </div>

            {/* Image Upload */}
            <div className="p-6">
              <ImageUpload
                onImageSelect={handleImageSelect}
                selectedImage={selectedImage}
                onCropClick={handleCropClick}
              />
            </div>

            {/* Background Options */}
            <div className="p-6 border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-slate-800/50">
              <BackgroundOptions
                backgroundType={backgroundType}
                onBackgroundTypeChange={handleBackgroundTypeChange}
                onBackgroundColorChange={handleBackgroundColorChange}
                passportSize={passportSize}
                onPassportSizeChange={setPassportSize}
                customWidth={customWidth}
                customHeight={customHeight}
                onCustomWidthChange={setCustomWidth}
                onCustomHeightChange={setCustomHeight}
                selectedCountry={selectedCountry}
                onCountryChange={setSelectedCountry}
                countriesList={countriesList}
              />

              {/* Show selected country dimensions */}
              {selectedCountrySize && (
                <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <p className="text-xs font-semibold text-blue-600 dark:text-blue-400">
                    {selectedCountry}: {selectedCountrySize.width} x {selectedCountrySize.height} px
                  </p>
                </div>
              )}

              {/* Auto-crop toggle (only in auto mode) */}
              {processingMode === 'auto' && (
                <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={autoCrop}
                      onChange={(e) => setAutoCrop(e.target.checked)}
                      className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                    />
                    <div>
                      <p className="text-sm font-bold text-gray-900 dark:text-white">Auto-Crop to Subject</p>
                      <p className="text-xs text-gray-400">Automatically focus on face/subject</p>
                    </div>
                  </label>
                </div>
              )}
            </div>

            {/* Process Button */}
            <div className="p-6 border-t border-gray-100 dark:border-gray-700">
              <button
                className={`w-full py-4 px-6 rounded-xl font-bold text-white transition-all shadow-md active:scale-95 flex items-center justify-center gap-2 ${
                  loading || !selectedImage
                    ? 'bg-gray-400 cursor-not-allowed'
                    : processingMode === 'auto'
                      ? 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 shadow-purple-500/20'
                      : 'bg-blue-600 hover:bg-blue-700 shadow-blue-500/20'
                }`}
                onClick={handleProcessImage}
                disabled={loading || !selectedImage}
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <FiZap /> {processingMode === 'auto' ? 'Auto Process' : 'Process Image'}
                  </>
                )}
              </button>

              {error && (
                <div className="mt-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-sm flex items-center gap-2">
                  <FiAlertCircle /> {error}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Preview & Result */}
        <div className="lg:col-span-7 space-y-6">
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-2">
            {!resultImageUrl ? (
              <div className="p-4 sm:p-6 min-h-[300px] flex flex-col">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-500 mb-4 px-2">Live Preview</h3>
                <div className="flex-1 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700 flex items-center justify-center bg-gray-50 dark:bg-slate-900/50">
                  <Preview
                    selectedImage={selectedImage}
                    backgroundType={backgroundType}
                    backgroundColor={backgroundColor}
                    resultImageUrl={resultImageUrl}
                    passportSize={passportSize}
                    customWidth={customWidth}
                    customHeight={customHeight}
                    selectedCountry={selectedCountry}
                  />
                </div>
              </div>
            ) : (
              <div className="p-4 sm:p-6 min-h-[300px]">
                <Result
                  resultImageUrl={resultImageUrl}
                  outputFilename={outputFilename}
                  onReset={handleReset}
                />
              </div>
            )}
          </div>

          {success && !resultImageUrl && (
            <div className="p-4 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300 flex items-center gap-2">
              <FiCheckCircle /> Image processed successfully!
            </div>
          )}
        </div>
      </div>

      {/* Crop Modal */}
      {showCropModal && selectedImage && (
        <ImageCrop
          imageFile={selectedImage}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
        />
      )}
    </div>
  );
}

export default SelfieEditor;
